import { ChangeDetectorRef, Directive, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { appSettings } from 'app/appSettings';
import { CDLineItemValidation, CDLogisticsVarietyPacking, CDPackingId, CDPackingStatus, CDPalletId, MaterialTypes, } from 'app/core/store/contract-details/contract-details.interface';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { Definitions } from 'app/core/store/definitions/definitions.interface';
import { CombinationMaterial, isCombinationMaterial, isPackingMaterial, isPalletMaterial, Material, PackingMaterial, PalletMaterial, } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { tick$$, util } from 'app/core/util/util';
import { PanelStatus } from 'app/modules/florder/florder-edit-container/florder-edit.interface';
import { Subscription } from 'rxjs';

interface LineRefParams {
  lineReferenceId: string;
  referenceRequired: boolean;
  mask: string;
}

export enum TypeOptions {
  pallet = 'pallet',
  packing = 'packing',
  combination = 'combination',
}

@Directive()
export abstract class FlorderEditMaterialComponent<MATERIAL extends Material> implements OnInit, OnChanges, OnDestroy {
  @Input() panelStatus: PanelStatus;
  @Input() lineItemValidation?: CDLineItemValidation;
  @Input() material: MATERIAL;
  @Input() materialTypes: MaterialTypes;
  @Input() saving: boolean;
  @Input() definitions: Definitions;

  @Output() saveMaterialClicked = new EventEmitter<MATERIAL>();
  @Output() nextClicked = new EventEmitter();

  pending = false;

  private subscriptions: Subscription[] = [];

  private setFormStateBasedOnMaterial: boolean = false;
  packingIds: Definition[] = [];
  palletIds: Definition[] = [];
  packingPerPalletList: Definition[] = [];
  logisticsVarietyPackings: Definition[] = [];
  packingStatusses: Definition[] = [];
  editMaterialForm: UntypedFormGroup;
  lineRefWarning: any;
  lineRefError: any;
  labelWidth = appSettings.LABEL_WIDTH;

  private previousFormValue: any;

  freezeFields = false;
  private listenToFormValueChangesSubscription: Subscription;

  translateService = inject(TranslateService);
  private cd = inject(ChangeDetectorRef);
  protected formBuilder = inject(UntypedFormBuilder);

  async ngOnInit() {
    await this.createForm();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.saving) {
    }
    if (changes.definitions) {
      await this.setFormStateIfRequired();
    }

    if (changes.material) {
      this.setFormStateBasedOnMaterial = true;
      await this.setFormStateIfRequired();
    }

    const materialTypesChanged = this.materialTypesChanged(changes);

    if ((materialTypesChanged || changes.material) && this.material && this.materialTypes) {
      let referenceToOriginalMaterial = this.material;
      if (this.materialTypes[this.material.type] === null || (this.material.type === TypeOptions.combination && this.materialTypes.combination.palletIds.length === 0)) {
        this.material = util.deepCopy(this.material);
        if (this.materialTypes.pallets) {
          this.material.type = TypeOptions.pallet;
        } else if (this.materialTypes.packings) {
          this.material.type = TypeOptions.packing;
        } else if (this.materialTypes.combination.palletIds.length) {
          this.material.type = TypeOptions.combination;
        }
        referenceToOriginalMaterial = this.material;
        this.editMaterialForm.get('type').patchValue(this.material.type);
        if (this.editMaterialForm) {
          this.typeChanged();
        }
      }
      await tick$$(); // fix issue with empty fields when edit existing material
      this.updateForm(referenceToOriginalMaterial);
      this.cd.markForCheck();
    }
  }

  private async tick() {
    this.cd.markForCheck();
    await tick$$();
  }

  /**
   * public: used in template
   */
  async typeChanged() {
    this.freezeFields = true;
    await this.tick();

    switch (this.editMaterialForm.get('type').value.toLowerCase()) {
      case TypeOptions.packing:
        this.setPackingIds();
        break;
      case TypeOptions.pallet:
        this.setPalletIds();
        break;
      case TypeOptions.combination:
        this.setPalletIds();
        break;
    }
    await this.tick();
    this.freezeFields = false;
    this.cd.markForCheck();
  }

  palletIdChanged() {
    switch (this.editMaterialForm.get('type').value) {
      case TypeOptions.combination:
        this.setPackingIds();
        break;
      case TypeOptions.pallet:
        this.setPackingStatusses();
        break;
    }
  }

  packingIdChanged() {
    switch (this.editMaterialForm.get('type').value) {
      case TypeOptions.packing:
      case TypeOptions.combination:
        this.setPackingStatusses();
        break;
    }
  }

  packingStatusChanged() {
    switch (this.editMaterialForm.get('type').value) {
      case TypeOptions.pallet:
      case TypeOptions.packing:
      case TypeOptions.combination:
        this.setLogisticsvarietyPackings();
        break;
    }
  }

  logisticsVarietyPackingChanged() {
    switch (this.editMaterialForm.get('type').value) {
      case TypeOptions.combination:
        this.setPackingPerPalletList();
        break;
    }
  }

  private async setFormStateIfRequired() {
    if (this.setFormStateBasedOnMaterial || (!this.editMaterialForm && this.material)) {
      this.setFormStateBasedOnMaterial = false;
      this.editMaterialForm && await this.createForm();
    }
  }

  // Override below methods from parent because no checks on contract
  protected setPalletIds() {
    let children = [];
    let type = this.editMaterialForm.get('type').value;
    let cascadingFn;
    switch (type) {
      case TypeOptions.pallet:
        // convert NO_LC key to more meaningful name
        children =
          this.materialTypes &&
          (this.materialTypes.pallets || []).map(pallet => {
            return {
              ...pallet,
              name: pallet.id === 'NO_LC' ? this.translateService.instant('ORDERS.MATERIAL.LABELS.NO_LC') : pallet.name,
            };
          });
        cascadingFn = this.setPackingStatusses.bind(this);
        break;
      case TypeOptions.combination:
        children =
          this.materialTypes &&
          (this.materialTypes.combination.palletIds || []).map(pallet => {
            return {
              ...pallet,
              name: pallet.id === 'NO_LC' ? this.translateService.instant('ORDERS.MATERIAL.LABELS.NO_LC') : pallet.name,
            };
          });
        cascadingFn = this.setPackingIds.bind(this);
        break;
    }

    util.setSelectElements<Definition>({
      children,
      childControls: this.editMaterialForm.get('palletId'),
      cascade: cascadingFn,
      setter: elemnts => (this.palletIds = elemnts),
      nameForLogging: 'palletId'
    });
  }

  protected setPackingIds() {
    let type = this.editMaterialForm.get('type').value;
    let children = [];
    switch (type) {
      case TypeOptions.packing:
        children = (this.materialTypes && this.materialTypes.packings) || [];
        break;
      case TypeOptions.combination:
        const palletId = this.materialTypes && this.materialTypes.combination.palletIds.find(palletId => palletId.id === this.editMaterialForm.get('palletId').value);
        children = (palletId && palletId.packingIds) || [];
        break;
    }
    this.packingIds = util.setSelectElements<Definition>({
      children,
      childControls: this.editMaterialForm.get('packingId'),
      cascade: this.setPackingStatusses.bind(this),
      nameForLogging: 'packingId'
    });
  }

  protected setPackingStatusses() {
    let type = this.editMaterialForm.get('type').value;
    if (type === TypeOptions.combination) {
      const palletId: CDPalletId = this.materialTypes && this.materialTypes.combination.palletIds.find(palletId => palletId.id === this.editMaterialForm.get('palletId').value);
      const packingId: CDPackingId = palletId && palletId.packingIds.find(packingId => packingId.id === this.editMaterialForm.get('packingId').value);
      const children = (packingId && packingId.packingStatusses) || [];
      this.packingStatusses = util.setSelectElements<Definition>({
        children,
        childControls: this.editMaterialForm.get('packingStatus'),
        cascade: this.setLogisticsvarietyPackings.bind(this),
        nameForLogging: 'packingStatus'
      });
    }

    // for edit flow: packingStatus does not exist in form, so skip then
    if (type === TypeOptions.packing && this.editMaterialForm.get('packingStatus')) {
      const packingId: CDPackingId = this.materialTypes && this.materialTypes.packings.find(packingId => packingId.id === this.editMaterialForm.get('packingId').value);
      const children = (packingId && packingId.packingStatusses) || [];
      this.packingStatusses = util.setSelectElements<Definition>({
        children,
        childControls: this.editMaterialForm.get('packingStatus'),
        cascade: this.setLogisticsvarietyPackings.bind(this),
        nameForLogging: 'packingStatus'
      });
    }

    if (type === TypeOptions.pallet) {
      const palletId: CDPackingId = this.materialTypes && this.materialTypes.pallets.find(palletId => palletId.id === this.editMaterialForm.get('palletId').value);
      const children = (palletId && palletId.packingStatusses) || [];
      this.packingStatusses = util.setSelectElements<Definition>({
        children,
        childControls: this.editMaterialForm.get('packingStatus'),
        cascade: this.setLogisticsvarietyPackings.bind(this),
        nameForLogging: 'packingStatus'
      });
    }
  }

  protected setLogisticsvarietyPackings() {
    let type = this.editMaterialForm.get('type').value;
    if (type === TypeOptions.combination) {
      const palletId: CDPalletId = this.materialTypes && this.materialTypes.combination.palletIds.find(palletId => palletId.id === this.editMaterialForm.get('palletId').value);
      const packingId: CDPackingId = palletId && palletId.packingIds.find(packingId => packingId.id === this.editMaterialForm.get('packingId').value);
      const packingStatus: CDPackingStatus =
        packingId && packingId.packingStatusses && packingId.packingStatusses.find(packingStatus => packingStatus.id === this.editMaterialForm.get('packingStatus').value);
      const children = (packingStatus && packingStatus.logisticsVarietyPackings) || [];
      this.logisticsVarietyPackings = util.setSelectElements<Definition>({
        children,
        childControls: this.editMaterialForm.get('logisticsVarietyPacking'),
        cascade: this.setPackingPerPalletList.bind(this),
        nameForLogging: 'logisticsVarietyPacking'
      });
    }

    if (type === TypeOptions.packing) {
      const packingId: CDPackingId = this.materialTypes && this.materialTypes.packings.find(packingId => packingId.id === this.editMaterialForm.get('packingId').value);
      const packingStatus: CDPackingStatus =
        packingId && packingId.packingStatusses && packingId.packingStatusses.find(packingStatus => packingStatus.id === this.editMaterialForm.get('packingStatus').value);
      const children = (packingStatus && packingStatus.logisticsVarietyPackings) || [];
      this.logisticsVarietyPackings = util.setSelectElements<Definition>({
        children,
        childControls: this.editMaterialForm.get('logisticsVarietyPacking'),
        cascade: this.setPackingPerPalletList.bind(this),
        nameForLogging: 'logisticsVarietyPacking'
      });
    }

    if (type === TypeOptions.pallet) {
      const palletId: CDPackingId = this.materialTypes && this.materialTypes.pallets.find(palletId => palletId.id === this.editMaterialForm.get('palletId').value);
      const packingStatus: CDPackingStatus =
        palletId && palletId.packingStatusses && palletId.packingStatusses.find(packingStatus => packingStatus.id === this.editMaterialForm.get('packingStatus').value);
      const children = (packingStatus && packingStatus.logisticsVarietyPackings) || [];
      this.logisticsVarietyPackings = util.setSelectElements<Definition>({
        children,
        childControls: this.editMaterialForm.get('logisticsVarietyPacking'),
        cascade: this.setPackingPerPalletList.bind(this),
        nameForLogging: 'logisticsVarietyPacking'
      });
    }
  }

  protected setPackingPerPalletList() {
    let type = this.editMaterialForm.get('type').value;
    if (type === TypeOptions.combination) {
      const palletId: CDPalletId = this.materialTypes && this.materialTypes.combination.palletIds.find(palletId => palletId.id === this.editMaterialForm.get('palletId').value);
      const packingId: CDPackingId = palletId && palletId.packingIds.find(packingId => packingId.id === this.editMaterialForm.get('packingId').value);
      const packingStatus: CDPackingStatus =
        packingId && packingId.packingStatusses && packingId.packingStatusses.find(packingStatus => packingStatus.id === this.editMaterialForm.get('packingStatus').value);
      const logisticsVarietyPacking: CDLogisticsVarietyPacking =
        packingStatus && packingStatus.logisticsVarietyPackings.find(lvp => lvp.id === this.editMaterialForm.get('logisticsVarietyPacking').value);
      const children = (logisticsVarietyPacking && logisticsVarietyPacking.packingsPerPallets) || [];
      const packingsPerPalletControl = this.editMaterialForm.get('packingsPerPallet');
      const packingsPerPallet = packingsPerPalletControl.value;

      // Issue of packingsPerPallet-ID missing a leading 0 (should be fixed in SAP, but it's not)
      let found = children.find(c => c.id === packingsPerPallet);
      if (!found) {
        packingsPerPalletControl.setValue('0' + packingsPerPallet); // Add a '0' before
        found = children.find(c => c.id === packingsPerPallet);
      }

      this.packingPerPalletList = util.setSelectElements<Definition>({
        children,
        childControls: packingsPerPalletControl,
        nameForLogging: 'packingsPerPallet'
      });
    }
  }

  private updateForm(material: Material) {
    const startingType =
      this.material && this.material.type
        ? this.material.type
        : (this.materialTypes.combination.palletIds.length && TypeOptions.combination) || (this.materialTypes.packings.length && TypeOptions.packing) || TypeOptions.pallet;
    const palletMaterial: PalletMaterial = isPalletMaterial(this.material) ? this.material : null;
    const packingMaterial: PackingMaterial = isPackingMaterial(this.material) ? this.material : null;
    const combinationMaterial: CombinationMaterial = isCombinationMaterial(this.material) ? this.material : null;
    this.editMaterialForm.patchValue({
      internalId: this.material.internalId,
      type: startingType,
      palletId: (palletMaterial || combinationMaterial)?.palletId,
      isNew: this.material.isNew,
      packingId: (packingMaterial || combinationMaterial)?.packingId,
      packingStatus: this.material.packingStatus,
      logisticsVarietyPacking: this.material.logisticsVarietyPacking,
      packingsPerPallet: combinationMaterial?.packingsPerPallet?.toString(),
      numberOfPallets: (combinationMaterial || palletMaterial)?.numberOfPallets,
      packingQuantity: packingMaterial?.packingQuantity,
      poNumberItemLevel: this.material.poNumberItemLevel,
      poItemNumber: this.material.poItemNumber,
      contentId: this.material.contentId,
      lineReferenceId: this.material.lineReferenceId,
    });
    this.previousFormValue = this.editMaterialForm.value;
  }

  protected async createForm() {
    const startingType =
      this.material && this.material.type
        ? this.material.type
        : (this.materialTypes.combination.palletIds.length && TypeOptions.combination) || (this.materialTypes.packings.length && TypeOptions.packing) || TypeOptions.pallet;
    const palletMaterial: PalletMaterial = isPalletMaterial(this.material) ? this.material : null;
    const packingMaterial: PackingMaterial = isPackingMaterial(this.material) ? this.material : null;
    const combinationMaterial: CombinationMaterial = isCombinationMaterial(this.material) ? this.material : null;
    this.editMaterialForm = this.formBuilder.group({
      internalId: [this.material.internalId],
      type: [startingType, Validators.required],
      palletId: [(palletMaterial || combinationMaterial)?.palletId],
      isNew: [this.material.isNew],
      packingId: [(packingMaterial || combinationMaterial)?.packingId],
      packingStatus: [this.material.packingStatus],
      logisticsVarietyPacking: [this.material.logisticsVarietyPacking],
      packingsPerPallet: [combinationMaterial?.packingsPerPallet?.toString()],
      numberOfPallets: [(combinationMaterial || palletMaterial)?.numberOfPallets],
      packingQuantity: [packingMaterial?.packingQuantity],
      poNumberItemLevel: [this.material.poNumberItemLevel],
      poItemNumber: [this.material.poItemNumber],
      contentId: [this.material.contentId, this.lineItemValidation && this.lineItemValidation.content ? [Validators.required] : []],
      lineReferenceId: [this.material.lineReferenceId, this.lineItemValidation && this.lineItemValidation.reference ? [Validators.required] : []],
    });
    this.previousFormValue = this.editMaterialForm.value;

    this.listenToFormValueChanges();
    this.typeChanged();

    util.conditionalValidation(
      this.editMaterialForm,
      'palletId',
      [util.createCondition('type', [appSettings.MATERIAL_TYPE_PALLET, appSettings.MATERIAL_TYPE_COMBINATION])],
      [Validators.required]
    );
    util.conditionalValidation(
      this.editMaterialForm,
      'packingId',
      [util.createCondition('type', [appSettings.MATERIAL_TYPE_PACKING, appSettings.MATERIAL_TYPE_COMBINATION])],
      [Validators.required]
    );
    util.conditionalValidation(this.editMaterialForm, 'packingStatus', [util.createCondition('type', [appSettings.MATERIAL_TYPE_COMBINATION])], [Validators.required]);
    util.conditionalValidation(this.editMaterialForm, 'logisticsVarietyPacking', [util.createCondition('type', [appSettings.MATERIAL_TYPE_COMBINATION])], [Validators.required]);
    util.conditionalValidation(this.editMaterialForm, 'packingsPerPallet', [util.createCondition('type', appSettings.MATERIAL_TYPE_COMBINATION)], [Validators.required]);
    util.conditionalValidation(
      this.editMaterialForm,
      'numberOfPallets',
      [util.createCondition('type', [appSettings.MATERIAL_TYPE_PALLET, appSettings.MATERIAL_TYPE_COMBINATION])],
      [Validators.required]
    );
    util.conditionalValidation(this.editMaterialForm,
      'packingQuantity',
      [util.createCondition('type', appSettings.MATERIAL_TYPE_PACKING)],
      [Validators.required]
    );
    this.cd.markForCheck();
  }

  protected listenToFormValueChanges() {
    this.previousFormValue = { ...this.previousFormValue };
    if (this.listenToFormValueChangesSubscription) {
      this.listenToFormValueChangesSubscription.unsubscribe();
    }
    this.listenToFormValueChangesSubscription = this.editMaterialForm.valueChanges.subscribe((formValue) => {
      if (this.freezeFields) {
        return; // avoid cascades of setElements triggering this again
      }
      this.freezeFields = true;
      const previousFormValue = { ...this.previousFormValue };
      this.previousFormValue = formValue;
      if (this.propertyChanged('type', formValue, previousFormValue)) {
        this.typeChanged();
        this.freezeFields = true;
      }
      if ((this.isCombination() || this.isPallet()) && this.propertyChanged('palletId', formValue, previousFormValue)) {
        this.palletIdChanged();
      }
      if ((this.isCombination() || this.isPacking()) && this.propertyChanged('packingId', formValue, previousFormValue)) {
        this.packingIdChanged();
      }
      if (this.propertyChanged('packingStatus', formValue, previousFormValue)) {
        this.packingStatusChanged();
      }
      if (this.propertyChanged('logisticsVarietyPacking', formValue, previousFormValue)) {
        this.logisticsVarietyPackingChanged();
      }
      this.freezeFields = false;
    });
  }

  isPacking(): boolean {
    return this.editMaterialForm.controls.type.value === appSettings.MATERIAL_TYPE_PACKING;
  }

  isPallet(): boolean {
    return this.editMaterialForm.controls.type.value === appSettings.MATERIAL_TYPE_PALLET;
  }

  isCombination(): boolean {
    return this.editMaterialForm.controls.type.value === appSettings.MATERIAL_TYPE_COMBINATION;
  }

  nextClickedFn() {
    if (!this.editMaterialForm.invalid) {
      this.saveMaterialClicked.emit(this.editMaterialForm.value);
    } else {
      if (this.formIsEmpty()) {
        this.nextClicked.emit();
      }
    }
  }

  protected formIsEmpty() {
    const valueIsEmpty = val => {
      if (val === null || val === undefined) {
        return true;
      }
      if (typeof val === 'string' && val.trim() === '') {
        return true;
      }
      return false;
    };

    const form = this.editMaterialForm.value;
    const values = [
      form.lineReferenceId,
      form.palletId,
      form.packingStatus,
      form.logisticsVarietyPacking,
      form.packingsPerPallet,
      form.numberOfPallets,
      form.packingId,
      form.packingQuantity,
      form.contentId,
    ];
    return form.isNew && values.find(v => !valueIsEmpty(v)) === undefined;
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private propertyChanged(field: string, currentValues: { [key: string]: string }, previousValues: { [key: string]: string }) {
    const currentValue = (currentValues && currentValues[field]?.toString() || '').trim();
    const previousValue = (previousValues && previousValues[field]?.toString() || '').trim();
    return currentValue !== previousValue;
  }

  private materialTypesChanged(changes: SimpleChanges): boolean {
    let change: SimpleChange = changes.materialTypes;
    if (!change) {
      return false;
    }
    if (!change.previousValue || !change.currentValue) {
      return true;
    }

    const previous = change.previousValue as MaterialTypes;
    const current = change.currentValue as MaterialTypes;
    const definitionToIdMapper = (definition: Definition) => definition.id;
    return false
      // pallets
      || !util.arraysContainSameStrings(previous.pallets.map(definitionToIdMapper), current.pallets.map(definitionToIdMapper))
      // packings
      || !util.arraysContainSameStrings(previous.packings.map(definitionToIdMapper), current.packings.map(definitionToIdMapper))
      // combination -> palletIds
      || !util.arraysContainSameStrings(previous.combination.palletIds.map(definitionToIdMapper), current.combination.palletIds.map(definitionToIdMapper))
      // combination -> palletIds -> packingIds
      || !util.arraysContainSameStrings(previous.combination.palletIds.flatMap(palletId => palletId.packingIds).map(definitionToIdMapper), current.combination.palletIds.flatMap(palletId => palletId.packingIds).map(definitionToIdMapper))
      // combination -> palletIds -> packingIds -> packingStatusses
      || !util.arraysContainSameStrings(previous.combination.palletIds.flatMap(palletId => palletId.packingIds.flatMap(packingId => packingId.packingStatusses)).map(definitionToIdMapper), current.combination.palletIds.flatMap(palletId => palletId.packingIds.flatMap(packingId => packingId.packingStatusses)).map(definitionToIdMapper))
      // combination -> palletIds -> packingIds -> packingStatusses -> logisticsVarietyPackings
      || !util.arraysContainSameStrings(previous.combination.palletIds.flatMap(palletId => palletId.packingIds.flatMap(packingId => packingId.packingStatusses.flatMap(packingStatus => packingStatus.logisticsVarietyPackings))).map(definitionToIdMapper), current.combination.palletIds.flatMap(palletId => palletId.packingIds.flatMap(packingId => packingId.packingStatusses.flatMap(packingStatus => packingStatus.logisticsVarietyPackings))).map(definitionToIdMapper))
      // combination -> palletIds -> packingIds -> packingStatusses -> logisticsVarietyPackings -> packingsPerPallet
      || !util.arraysContainSameStrings(previous.combination.palletIds.flatMap(palletId => palletId.packingIds.flatMap(packingId => packingId.packingStatusses.flatMap(packingStatus => packingStatus.logisticsVarietyPackings.flatMap(lvp => lvp.packingsPerPallets)))).map(definitionToIdMapper), current.combination.palletIds.flatMap(palletId => palletId.packingIds.flatMap(packingId => packingId.packingStatusses.flatMap(packingStatus => packingStatus.logisticsVarietyPackings.flatMap(lvp => lvp.packingsPerPallets)))).map(definitionToIdMapper));
  }
}
